/**
 * ============================================
 * SAHINKAYA_GUNCELLEMESI - WISHLIST (FAVORİLER) SİSTEMİ
 * Tarih: Mart 2026
 * --------------------------------------------
 * Hibrit yaklaşım:
 * - Giriş yapmamış kullanıcılar: localStorage
 * - Giriş yapmış kullanıcılar: Shopify Customer Metafields (Storefront API)
 * - Giriş yapınca localStorage -> Metafields merge
 * ============================================
 */

class WishlistManager {
  constructor() {
    this.storageKey = 'wishlist_items';
    this.customerId = window.wishlistConfig?.customerId || null;
    this.storefrontToken = window.wishlistConfig?.storefrontToken || null;
    this.shopDomain = window.wishlistConfig?.shopDomain || window.Shopify?.shop || null;
    this.metafieldNamespace = 'custom';
    this.metafieldKey = 'wishlist';
    this._items = null;
    this._syncInProgress = false;
  }

  // ─── LOCAL STORAGE ───────────────────────────────────────────────────────────

  _getLocalItems() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey) || '[]');
    } catch {
      return [];
    }
  }

  _setLocalItems(items) {
    localStorage.setItem(this.storageKey, JSON.stringify([...new Set(items)]));
  }

  _clearLocal() {
    localStorage.removeItem(this.storageKey);
  }

  // ─── APP PROXY (METAFIELDS) ───────────────────────────────────────────────────

  async _getMetafieldItems() {
    if (!this.customerId) return null;
    try {
      // /apps/wishlist → Shopify proxy → https://wishlist-proxy-rho.vercel.app/api/wishlist
      const res = await fetch('/apps/wishlist', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) return null;
      const data = await res.json();
      return Array.isArray(data.items) ? data.items : null;
    } catch {
      return null;
    }
  }

  async _setMetafieldItems(items) {
    if (!this.customerId) return;
    await this._saveViaProxy(items);
  }

  async _saveViaProxy(items) {
    // Shopify App Proxy → /apps/wishlist → Vercel Function /api/wishlist
    try {
      await fetch('/apps/wishlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
    } catch {
      // Proxy mevcut değilse sessizce geç, localStorage zaten güncel
    }
  }

  // ─── CORE API ────────────────────────────────────────────────────────────────

  async getItems() {
    if (this._items !== null) return this._items;

    if (this.customerId) {
      const remote = await this._getMetafieldItems();
      if (remote !== null) {
        this._items = remote;
        return this._items;
      }
    }

    this._items = this._getLocalItems();
    return this._items;
  }

  async addItem(handle) {
    const items = await this.getItems();
    if (!items.includes(handle)) {
      items.push(handle);
      this._items = items;
      this._persist();
    }
    this._updateUI(handle, true);
    this._dispatchChange();
  }

  async removeItem(handle) {
    const items = await this.getItems();
    this._items = items.filter(h => h !== handle);
    this._persist();
    this._updateUI(handle, false);
    this._dispatchChange();
  }

  async toggleItem(handle) {
    const items = await this.getItems();
    if (items.includes(handle)) {
      await this.removeItem(handle);
    } else {
      await this.addItem(handle);
    }
  }

  async hasItem(handle) {
    const items = await this.getItems();
    return items.includes(handle);
  }

  async getCount() {
    const items = await this.getItems();
    return items.length;
  }

  _persist() {
    const items = this._items || [];
    this._setLocalItems(items);
    if (this.customerId) {
      this._setMetafieldItems(items);// App Proxy üzerinden Metafield'a yaz
    }
  }

  // ─── MERGE (GİRİŞ SONRASI) ───────────────────────────────────────────────────

  async mergeLocalToMetafield() {
    if (this._syncInProgress) return;
    this._syncInProgress = true;
    const local = this._getLocalItems();
    if (local.length === 0) { this._syncInProgress = false; return; }

    const remote = await this._getMetafieldItems() || [];
    const merged = [...new Set([...remote, ...local])];
    this._items = merged;
    this._setLocalItems(merged);
    await this._setMetafieldItems(merged);
    this._clearLocal();
    this._syncInProgress = false;
    this._dispatchChange();
  }

  // ─── UI UPDATES ──────────────────────────────────────────────────────────────

  _updateUI(handle, isActive) {
    document.querySelectorAll(`.wishlist-btn[data-handle="${handle}"]`).forEach(btn => {
      btn.classList.toggle('wishlist-btn--active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
      // Label toggle CSS ile yapılıyor (.wishlist-btn--active class'ı tetikler)
    });
  }

  async updateAllButtons() {
    const items = await this.getItems();
    document.querySelectorAll('.wishlist-btn').forEach(btn => {
      const handle = btn.dataset.handle;
      const isActive = items.includes(handle);
      btn.classList.toggle('wishlist-btn--active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
    });
  }

  async updateCountBadges() {
    const count = await this.getCount();
    document.querySelectorAll('.wishlist-count').forEach(el => {
      el.textContent = count;
      el.classList.toggle('wishlist-count--hidden', count === 0);
    });
  }

  _dispatchChange() {
    this.updateCountBadges();
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items: this._items } }));
  }

  // ─── INIT ─────────────────────────────────────────────────────────────────────

  async init() {
    await this.updateAllButtons();
    await this.updateCountBadges();

    // Giriş yapmış kullanıcı için localStorage -> Metafield merge
    if (this.customerId) {
      await this.mergeLocalToMetafield();
    }

    // Buton click listener
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('.wishlist-btn');
      if (!btn) return;
      e.preventDefault();
      const handle = btn.dataset.handle;
      if (!handle) return;
      btn.classList.add('wishlist-btn--loading');
      await this.toggleItem(handle);
      btn.classList.remove('wishlist-btn--loading');
    });
  }
}

// ─── WISHLIST PAGE RENDERER ───────────────────────────────────────────────────

class WishlistPage {
  constructor(manager) {
    this.manager = manager;
    this.container = document.getElementById('wishlist-products-grid');
    this.emptyState = document.getElementById('wishlist-empty-state');
    this.loadingState = document.getElementById('wishlist-loading-state');
  }

  async render() {
    if (!this.container) return;
    this._showLoading(true);

    const items = await this.manager.getItems();

    if (items.length === 0) {
      this._showLoading(false);
      this._showEmpty(true);
      return;
    }

    const cards = await this._fetchProductCards(items);
    this._showLoading(false);

    if (cards.length === 0) {
      this._showEmpty(true);
      return;
    }

    this._showEmpty(false);
    this.container.innerHTML = cards.join('');

    // Favorilerim sayfasındaki kalpleri aktif göster
    await this.manager.updateAllButtons();
  }

  async _fetchProductCards(handles) {
    const promises = handles.map(handle =>
      fetch(`/products/${handle}?sections=wishlist-product-card`)
        .then(r => r.ok ? r.json() : null)
        .then(data => data?.['wishlist-product-card'] || null)
        .catch(() => null)
    );
    const results = await Promise.all(promises);
    return results.filter(Boolean);
  }

  _showLoading(show) {
    if (this.loadingState) this.loadingState.hidden = !show;
  }

  _showEmpty(show) {
    if (this.emptyState) this.emptyState.hidden = !show;
    if (this.container) this.container.hidden = show;
  }
}

// ─── BOOTSTRAP ───────────────────────────────────────────────────────────────

const wishlist = new WishlistManager();
window.Wishlist = wishlist;

document.addEventListener('DOMContentLoaded', async () => {
  await wishlist.init();

  // Wishlist sayfasındaysa renderer'ı başlat
  if (document.getElementById('wishlist-products-grid')) {
    const page = new WishlistPage(wishlist);
    await page.render();
  }

  // Favorilerim sayfasında herhangi bir grid'de favori kaldırıldığında kartı sil
  const wishlistPage = document.querySelector('.wishlist-page');
  if (wishlistPage) {
    document.addEventListener('wishlist:change', async (e) => {
      const currentItems = e.detail?.items || await wishlist.getItems();

      // Tüm wishlist butonlarını tara (hem server hem JS grid)
      wishlistPage.querySelectorAll('.wishlist-btn[data-handle]').forEach(btn => {
        const handle = btn.dataset.handle;
        if (!currentItems.includes(handle)) {
          // Grid'in (.product-list) direkt child'ına kadar yukarı çık
          const grids = [...wishlistPage.querySelectorAll('.product-list')];
          let el = btn.parentElement;
          while (el) {
            const parent = el.parentElement;
            if (parent && grids.includes(parent)) {
              el.remove();
              break;
            }
            el = parent;
          }
        }
      });

      // Tüm grids boşsa → boş durum göster
      const allGrids = wishlistPage.querySelectorAll('.product-list');
      const allEmpty = [...allGrids].every(g => g.children.length === 0 || g.hidden);
      if (allEmpty) {
        allGrids.forEach(g => { g.hidden = true; });
        const emptyState = document.getElementById('wishlist-empty-state');
        if (emptyState) emptyState.hidden = false;
      }
    });
  }
});
